import { CreateHealthRecordBodyHealthRecordGenderEnum } from "@syadem/kairos-pro-js";
import { type Evc } from "@syadem/evc-js";
import { useEffect, useMemo, useState } from "react";
import { getTranslatedFieldByLocale } from "../../../../utils/translatedFields";
import { dayjs } from "../../../../utils/dayjs";
import { CapitalizeFirstNames } from "../../../../utils/name";
import { parseEvcToken } from "../../../components/evc/parseEvcToken";
import { useDaphne } from "../../../hooks/useDaphne";
import { useI18n } from "../../../hooks/useI18n";
import { getDiseasesNames } from "../../../hooks/useVaccinesWithDiseases";
import { VaccinationToImport } from "./types";

type EvcImportDraftStatus = "loading" | "ready" | "error";

export function useEvcImportDraft(evcToken: string | null) {
  const { locale } = useI18n();
  const daphne = useDaphne();
  const [status, setStatus] = useState<EvcImportDraftStatus>("loading");
  const [parsedEvc, setParsedEvc] = useState<Evc | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState<ReturnType<typeof dayjs.utc> | null>(null);
  const [gender, setGender] = useState<CreateHealthRecordBodyHealthRecordGenderEnum | "">("");
  const [zipCode, setZipCode] = useState("");

  useEffect(() => {
    if (!evcToken) return;

    let cancelled = false;
    setStatus("loading");
    setParseError(null);

    (async () => {
      try {
        const evc = await parseEvcToken(evcToken);
        if (cancelled) return;

        setParsedEvc(evc);
        setFirstName(CapitalizeFirstNames(evc.name.firstName));
        setLastName(evc.name.lastName.toUpperCase());
        setBirthDate(dayjs.utc(evc.dateOfBirth));
        setGender((evc.metadata?.gender as CreateHealthRecordBodyHealthRecordGenderEnum | undefined) ?? "");
        setZipCode("");
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        setParsedEvc(null);
        setParseError(error instanceof Error ? error.message : null);
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [evcToken]);

  const vaccinations = useMemo<VaccinationToImport[]>(() => {
    if (!parsedEvc) return [];

    return (parsedEvc.vaccinations || []).map((vaccination) => {
      const vaccine = daphne.repositories.vaccines.all().find((candidate) => candidate.code === vaccination.nuvaCode);
      const vaccineName = vaccine ? getTranslatedFieldByLocale(locale, vaccine.name) : `NUVA ${vaccination.nuvaCode}`;
      const diseases = vaccine ? daphne.queries.diseasesByVaccine(vaccine) : [];
      const diseasesLabel = getDiseasesNames(diseases, locale || "en");

      return {
        id: vaccination.id,
        nuvaCode: vaccination.nuvaCode,
        vaccineName: vaccineName || `NUVA ${vaccination.nuvaCode}`,
        diseasesLabel: diseasesLabel || "-",
        countryCode: vaccination.countryCode,
        performedOn: vaccination.performedOn.toISOString().slice(0, 10),
        booster: vaccination.booster ?? false,
      };
    });
  }, [daphne, locale, parsedEvc]);

  return {
    status,
    parsedEvc,
    parseError,
    vaccinations,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    birthDate,
    setBirthDate,
    gender,
    setGender,
    zipCode,
    setZipCode,
  };
}
